import {
  Box,
  Button,
  Center,
  Flex,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  Document,
  Image,
  Page,
  PDFViewer,
  Text as PDFText,
  View,
} from "@react-pdf/renderer";
import { useTransactions } from "api/transactions";
import logoWithName from "assets/logo_with_name.png";
import { format, sub } from "date-fns";
import DateInput from "generics/DateInput";
import PageTitle from "generics/PageTitle";
import ToolbarPortal from "generics/ToolbarPortal";
import { useAuth } from "providers/AuthProvider";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { FaFilePdf } from "react-icons/fa";
import { pdfjs } from "react-pdf";
import styles from "styles/pdfStyles";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

export default function TransactionsPage() {
  const { lab } = useAuth();
  const [pdf, setPdf] = useState(false);
  const [form, setForm] = useState<{
    start: string;
    end: string;
    payment_method: string;
  }>({
    start: format(sub(new Date(), { weeks: 1 }), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
    payment_method: "all",
  });
  const { register, handleSubmit } = useForm({
    defaultValues: {
      start: form.start,
      end: form.end,
      payment_method: form.payment_method,
    },
  });

  const transactionsQuery = useTransactions([
    "transactions",
    { start: form.start, end: form.end },
  ]);

  function getTotal(transactions: any) {
    return transactions?.reduce(
      (prev: any, curr: any) => prev + curr.amount - (curr.deliveryCharge || 0),
      0
    );
  }

  function paymentMethod(transaction: any) {
    let value: String = "";
    if (transaction.submitSampleAtLab) {
      value = "At Lab";
    } else {
      if (transaction.online) {
        value = "Online";
      } else if (transaction.collectingBoyId) {
        value = "At Pickup";
      }
    }
    return <Text>{value}</Text>;
  }

  let transactions = transactionsQuery.data?.filter((i: any) => {
    if (form.payment_method === "all") return i;
    if (form.payment_method === "lab") return i.submitSampleAtLab;
    if (form.payment_method === "online") return i.online;
    if (form.payment_method === "pickup") return !!i.collectingBoyId;
    return false;
  });

  function getPdfContent() {
    return (
      <Page wrap style={styles.body}>
        <View
          style={{ ...styles.row, display: "flex", justifyContent: "center" }}
        >
          <Image src={logoWithName} style={styles.logo}></Image>
        </View>
        <PDFText
          style={styles.title}
        >{`Transaction from ${form.start} to ${form.end}`}</PDFText>
        <PDFText style={styles.header}>{lab?.name}</PDFText>

        <View style={styles.table}>
          {/* TableHeader */}
          <View style={styles.tableRow} wrap={false}>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>From</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Txn Id</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Paid At</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Appointment At</PDFText>
            </View>
            <View style={styles.tableCol}>
              <PDFText style={styles.tableCell}>Amount</PDFText>
            </View>
          </View>
          {/* TableContent */}
          {transactions?.map((transaction: any, index: number) => {
            return (
              <View style={styles.tableRow} wrap={false} key={transaction.id}>
                <View style={styles.tableCol}>
                  <PDFText style={styles.tableCell}>
                    {transaction.from}{" "}
                  </PDFText>
                </View>
                <View style={styles.tableCol}>
                  <PDFText style={{ ...styles.tableCell, fontSize: 8 }}>
                    {transaction.id}
                  </PDFText>
                </View>
                <View style={styles.tableCol}>
                  <PDFText style={styles.tableCell}>
                    {format(transaction.createdAt, "E, do LLL, yyyy h:mm a")}
                  </PDFText>
                </View>
                <View style={styles.tableCol}>
                  <PDFText style={styles.tableCell}>
                    {transaction.scheduledCollectionTime
                      ? format(
                          transaction.scheduledCollectionTime,
                          "E, do LLL, yyyy h:mm a"
                        )
                      : "--"}
                  </PDFText>
                </View>

                <View style={styles.tableCol}>
                  <PDFText style={styles.tableCell}>
                    Rs. {transaction.amount - (transaction.deliveryCharge || 0)}
                  </PDFText>
                </View>
              </View>
            );
          })}
          <View style={styles.tableRow} wrap={false}>
            <View style={{ ...styles.tableCol, width: "80%" }}>
              <PDFText style={styles.tableCell}>All Total</PDFText>
            </View>
            <View style={{ ...styles.tableCol, width: "20%" }}>
              <PDFText style={styles.tableCell}>
                Rs.{" "}
                {transactions?.reduce(
                  (acc: any, curr: any) =>
                    acc + curr.amount - (curr.deliveryCharge || 0),
                  0
                )}
              </PDFText>
            </View>
          </View>
        </View>
        <View style={styles.column}>
          <PDFText style={{ ...styles.normalText, marginTop: 50 }}>
            Thank you for using cura health
          </PDFText>
          <View style={styles.divider}>
            <PDFText></PDFText>
          </View>
          <PDFText style={styles.contact}>Contact</PDFText>
          <PDFText style={styles.normalText}>
            Near Aqua Park, Tintoliya, Biratnagar-10, Morang, Nepal
          </PDFText>
          <PDFText style={styles.normalText}>
            Phone number: +977-9823314489,facebook: /curahealthapp
          </PDFText>
          <PDFText style={styles.normalText}>info.curahealth@gmail.com</PDFText>
        </View>
      </Page>
    );
  }

  return (
    <Box>
      <ToolbarPortal>
        <PageTitle flexGrow={1}>Transactions</PageTitle>
      </ToolbarPortal>
      <Flex px={12} py={6} justifyContent="space-between">
        <form
          onSubmit={handleSubmit((v) => {
            setPdf(false);
            setForm(v);
          })}
        >
          <Stack direction="row" align="center" spacing={8}>
            <Stack direction="row" align="center">
              <DateInput name="start" ref={register} />
              <Text color="black.500" fontWeight="500">
                To
              </Text>
              <DateInput name="end" ref={register} />
            </Stack>
            <Select w="auto" ref={register} name="payment_method">
              <option value="all">All Payment Methods</option>
              <option value="online">Online</option>
              <option value="pickup">At Pickup</option>
              <option value="lab">At Lab</option>
            </Select>
            <Button type="submit">Filter</Button>
          </Stack>
        </form>
        <Button
          leftIcon={<FaFilePdf />}
          hidden={transactions?.length === 0}
          colorScheme="gray"
          variant="outline"
          onClick={() => setPdf(true)}
        >
          Download PDF
        </Button>
      </Flex>
      {transactionsQuery.isLoading ? (
        <Box h="300px">
          <Center w="100%" h="100%">
            <Spinner size="lg" />
          </Center>
        </Box>
      ) : transactions?.length === 0 ? (
        <Center h="200px" w="100%">
          <Text fontSize="xl" color="gray.500">
            No Transactions!
          </Text>
        </Center>
      ) : (
        <Box px={12}>
          <Table>
            <Thead>
              <Tr>
                <Th>From</Th>
                <Th>Txn Id</Th>
                <Th>Paid At</Th>
                <Th>Appointment At</Th>
                <Th>Payment Method</Th>
                <Th>Amount</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions?.map((transaction) => (
                <Tr key={transaction.id} color="gray.500">
                  <Td>
                    <Text>{transaction.from}</Text>
                  </Td>
                  <Td>
                    <Text fontWeight="500" fontSize="sm" color="black.500">
                      {transaction.id}
                    </Text>
                  </Td>
                  <Td>
                    <Text>
                      {format(transaction.createdAt, "E, do LLL, yyyy h:mm a")}
                    </Text>
                  </Td>
                  <Td>
                    <Text>
                      {transaction.scheduledCollectionTime
                        ? format(
                            transaction.scheduledCollectionTime,
                            "E, do LLL, yyyy h:mm a"
                          )
                        : "--"}
                    </Text>
                  </Td>
                  <Td>
                    <Text>{paymentMethod(transaction)}</Text>
                  </Td>
                  <Td>
                    <Text color="black.500" fontWeight="500" textAlign="right">
                      Rs.{" "}
                      {transaction.amount - (transaction.deliveryCharge || 0)}
                    </Text>
                  </Td>
                </Tr>
              ))}
              <Tr fontWeight="500" fontSize="sm" color="black.500">
                <Td colSpan={5}>TOTAL</Td>
                <Td textAlign="right">Rs. {getTotal(transactions)}</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      )}
      {pdf && (
        <Flex direction="column">
          <PDFViewer
            style={{
              height: "100vh",
            }}
          >
            <Document>{getPdfContent()}</Document>
          </PDFViewer>
        </Flex>
      )}
    </Box>
  );
}
